import React, { useState, useEffect, useCallback } from "react";
import {
  PlusCircle,
  Trash2,
  Edit2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  X,
  Star,
  Calendar,
  Smile,
  Link,
  Hash,
  Video,
  Play,
  Youtube,
  ExternalLink,
} from "lucide-react";
import marathiService from '../../services/marathiService';


// Custom Alert Component
const Alert = ({ children, variant, className = "" }) => {
  const baseStyles = "flex w-full rounded-lg border p-4";
  const variantStyles =
    variant === "destructive"
      ? "border-red-200 bg-red-50 text-red-600"
      : "border-gray-200 bg-gray-50 text-gray-600";

  return (
    <div className={`${baseStyles} ${variantStyles} ${className}`}>
      {children}
    </div>
  );
};

const AlertDescription = ({ children, className = "" }) => (
  <div className={`text-sm ${className}`}>{children}</div>
);

// Feeling options
const feelingOptions = [
  { value: "happy", label: "Happy" },
  { value: "excited", label: "Excited" },
  { value: "nostalgic", label: "Nostalgic" },
  { value: "proud", label: "Proud" },
  { value: "thoughtful", label: "Thoughtful" },
  { value: "inspired", label: "Inspired" },
  { value: "calm", label: "Calm" },
  { value: "grateful", label: "Grateful" },
  { value: "determined", label: "Determined" },
  { value: "hopeful", label: "Hopeful" },
  { value: "other", label: "Other" },
];

// Video source options
const videoSourceOptions = [
  { value: "youtube", label: "YouTube" },
  { value: "vimeo", label: "Vimeo" },
  { value: "facebook", label: "Facebook" },
  { value: "instagram", label: "Instagram" },
  { value: "other", label: "Other" },
];

// Function to extract video ID from YouTube URL
const getYoutubeVideoId = (url) => {
  if (!url) return null;

  // Regular expressions for different YouTube URL formats
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);

  return match && match[2].length === 11 ? match[2] : null;
};

// Function to get YouTube thumbnail from video ID
const getYoutubeThumbnail = (videoId) => {
  if (!videoId) return "";
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
};

// Function to create YouTube embed URL
const getYoutubeEmbedUrl = (videoId) => {
  if (!videoId) return "";
  return `https://www.youtube.com/embed/${videoId}`;
};

// Function to extract video ID from Vimeo URL
const getVimeoVideoId = (url) => {
  if (!url) return null;

  // Regular expressions for different Vimeo URL formats
  const regExp =
    /vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/([^/]*)\/videos\/|)(\d+)(?:|\/\?)/;
  const match = url.match(regExp);

  return match ? match[2] : null;
};

// Video Embed Component
const VideoEmbed = ({ video }) => {
  if (!video || !video.videoUrl) return null;

  if (video.videoType === "youtube") {
    const videoId = getYoutubeVideoId(video.videoUrl);
    if (!videoId)
      return <div className="text-red-500">Invalid YouTube URL</div>;

    return (
      <div className="aspect-w-16 aspect-h-9">
        <iframe
          src={getYoutubeEmbedUrl(videoId)}
          title={video.title || "YouTube Video"}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        ></iframe>
      </div>
    );
  }

  if (video.videoType === "vimeo") {
    const videoId = getVimeoVideoId(video.videoUrl);
    if (!videoId) return <div className="text-red-500">Invalid Vimeo URL</div>;

    return (
      <div className="aspect-w-16 aspect-h-9">
        <iframe
          src={`https://player.vimeo.com/video/${videoId}`}
          title={video.title || "Vimeo Video"}
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        ></iframe>
      </div>
    );
  }

  // For other video types, just show a link
  return (
    <div className="p-4 bg-gray-100 rounded-lg text-center">
      <ExternalLink className="w-8 h-8 mx-auto mb-2 text-red-600" />
      <a
        href={video.videoUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-red-600 hover:underline"
      >
        Watch Video: {video.title || video.videoUrl}
      </a>
    </div>
  );
};

const MarathiVideoGallery = () => {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(6); // Reduced for larger cards
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showFeaturedOnly] = useState(false);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);

  // Form Data
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    videos: [],
    hashtags: [],
    feeling: "happy",
    links: [],
    featured: false,
  });

  // Temp state for inputs
  const [newHashtag, setNewHashtag] = useState("");
  const [newVideoUrl, setNewVideoUrl] = useState("");
  const [newVideoTitle, setNewVideoTitle] = useState("");
  const [newVideoDescription, setNewVideoDescription] = useState("");
  const [newVideoType, setNewVideoType] = useState("youtube");
  const [newLinkTitle, setNewLinkTitle] = useState("");
  const [newLinkUrl, setNewLinkUrl] = useState("");

  const fetchGalleries = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      // FIXED: Changed from marathiService.marathiBlogs.videoGallery to marathiService.videoGallery
      const response = await marathiService.videoGallery.getAll({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        featured: showFeaturedOnly || undefined,
      });
      
      // Handle different response formats
      if (Array.isArray(response)) {
        setGalleries(response);
        setTotalPages(Math.ceil(response.length / itemsPerPage) || 1);
      } else if (response && response.data) {
        setGalleries(response.data);
        setTotalPages(response.totalPages || Math.ceil(response.data.length / itemsPerPage) || 1);
      } else {
        console.error('Unexpected response format:', response);
        setGalleries([]);
        setTotalPages(1);
      }
    } catch (err) {
      console.error('Error fetching video galleries:', err);
      setError(err.message || "Failed to fetch video galleries");
      setGalleries([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm, showFeaturedOnly]);

  useEffect(() => {
    fetchGalleries();
  }, [fetchGalleries]);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      // FIXED: Changed from marathiService.marathiBlogs.videoGallery to marathiService.videoGallery
      await marathiService.videoGallery.delete(selectedGallery._id);
      setSuccess("Video gallery deleted successfully!");
      setShowDeleteModal(false);
      fetchGalleries();
    } catch (err) {
      console.error('Error deleting video gallery:', err);
      setError(err.message || "Failed to delete video gallery");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form data
    if (!formData.title.trim()) {
      setError("Title is required");
      return;
    }
    
    if (formData.videos.length === 0) {
      setError("At least one video is required");
      return;
    }
    
    try {
      setLoading(true);
      setError("");
      
      // FIXED: Changed from marathiService.marathiBlogs.videoGallery to marathiService.videoGallery
      if (selectedGallery) {
        await marathiService.videoGallery.update(
          selectedGallery._id,
          formData
        );
        setSuccess("Video gallery updated successfully!");
      } else {
        await marathiService.videoGallery.create(formData);
        setSuccess("Video gallery created successfully!");
      }
      setShowFormModal(false);
      fetchGalleries();
    } catch (err) {
      console.error('Error saving video gallery:', err);
      setError(err.message || "Failed to save video gallery");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const addHashtag = () => {
    if (newHashtag.trim() && !formData.hashtags.includes(newHashtag.trim())) {
      setFormData({
        ...formData,
        hashtags: [...formData.hashtags, newHashtag.trim()],
      });
      setNewHashtag("");
    }
  };

  const removeHashtag = (tagToRemove) => {
    setFormData({
      ...formData,
      hashtags: formData.hashtags.filter((tag) => tag !== tagToRemove),
    });
  };

  const addVideo = () => {
    if (newVideoUrl.trim()) {
      let thumbnailUrl = "";

      // Auto-generate thumbnail for YouTube videos
      if (newVideoType === "youtube") {
        const videoId = getYoutubeVideoId(newVideoUrl);
        if (videoId) {
          thumbnailUrl = getYoutubeThumbnail(videoId);
        }
      }

      const newVideo = {
        videoUrl: newVideoUrl,
        videoType: newVideoType,
        title: newVideoTitle.trim(),
        description: newVideoDescription.trim(),
        thumbnailUrl, // May be empty for non-YouTube videos
      };

      setFormData({
        ...formData,
        videos: [...formData.videos, newVideo],
      });

      // Reset inputs
      setNewVideoUrl("");
      setNewVideoTitle("");
      setNewVideoDescription("");
      // Keep the video type as is for convenience
    }
  };

  const removeVideo = (index) => {
    const updatedVideos = [...formData.videos];
    updatedVideos.splice(index, 1);
    setFormData({
      ...formData,
      videos: updatedVideos,
    });
  };

  const addLink = () => {
    if (newLinkUrl.trim() && newLinkTitle.trim()) {
      const newLink = {
        title: newLinkTitle.trim(),
        url: newLinkUrl.trim(),
      };

      setFormData({
        ...formData,
        links: [...formData.links, newLink],
      });

      // Reset inputs
      setNewLinkTitle("");
      setNewLinkUrl("");
    }
  };

  const removeLink = (index) => {
    const updatedLinks = [...formData.links];
    updatedLinks.splice(index, 1);
    setFormData({
      ...formData,
      links: updatedLinks,
    });
  };

  const openEditModal = (gallery) => {
    setSelectedGallery(gallery);
    setFormData({
      title: gallery.title || "",
      description: gallery.description || "",
      videos: gallery.videos || [],
      hashtags: gallery.hashtags || [],
      feeling: gallery.feeling || "happy",
      links: gallery.links || [],
      featured: gallery.featured || false,
    });
    setShowFormModal(true);
  };

  const openCreateModal = () => {
    setSelectedGallery(null);
    setFormData({
      title: "",
      description: "",
      videos: [],
      hashtags: [],
      feeling: "happy",
      links: [],
      featured: false,
    });
    setShowFormModal(true);
  };

  const openPreviewModal = (gallery, index = 0) => {
    setSelectedGallery(gallery);
    setSelectedVideoIndex(index);
    setShowPreviewModal(true);
  };

  const toggleFeatureStatus = async (gallery) => {
    try {
      setLoading(true);
      // FIXED: Changed from marathiService.marathiBlogs.videoGallery to marathiService.videoGallery
      await marathiService.videoGallery.toggleFeature(gallery._id);
      setSuccess("Video gallery feature status updated!");
      fetchGalleries();
    } catch (err) {
      console.error('Error toggling feature status:', err);
      setError(err.message || "Failed to update feature status");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Function to get video thumbnail
  const getVideoThumbnail = (video) => {
    if (video.thumbnailUrl) return video.thumbnailUrl;

    if (video.videoType === "youtube") {
      const videoId = getYoutubeVideoId(video.videoUrl);
      if (videoId) return getYoutubeThumbnail(videoId);
    }

    // Default thumbnail for other video types
    return "https://placehold.co/600x400/e2e8f0/64748b?text=Video";
  };

  return (
    <div className="min-h-screen bg-red-50 p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-black">
          Marathi Video Gallery Management
        </h1>
        <button
          onClick={openCreateModal}
          className="flex items-center px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 transition-colors"
        >
          <PlusCircle className="mr-2" size={20} />
          Add New Video Gallery
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-4 bg-green-50 text-green-700 border-green-200">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="animate-spin text-red-600" size={32} />
        </div>
      )}

      {/* Gallery Grid */}
      {!loading && !error && (
        <div className="space-y-6">
          {/* Gallery Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleries.length > 0 ? (
              galleries.map((gallery) => (
                <div
                  key={gallery._id}
                  className="bg-white rounded-lg shadow-lg overflow-hidden border border-red-200 hover:shadow-xl transition-shadow"
                >
                  {/* Video Preview Thumbnail */}
                  <div className="relative h-56 overflow-hidden bg-gray-100">
                    {gallery.videos && gallery.videos.length > 0 ? (
                      <>
                        <img
                          src={getVideoThumbnail(gallery.videos[0])}
                          alt={gallery.title}
                          className="w-full h-full object-cover cursor-pointer"
                          onClick={() => openPreviewModal(gallery)}
                        />
                        <div
                          className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center cursor-pointer"
                          onClick={() => openPreviewModal(gallery)}
                        >
                          <div className="bg-red-600 bg-opacity-80 rounded-full p-3">
                            <Play className="text-white" size={32} />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full">
                        <Video className="text-gray-400" size={48} />
                        <p className="text-gray-500 mt-2">No videos</p>
                      </div>
                    )}

                    {gallery.videos && gallery.videos.length > 1 && (
                      <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded-lg">
                        +{gallery.videos.length - 1} more
                      </div>
                    )}

                    {gallery.featured && (
                      <div className="absolute top-2 left-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full bg-yellow-100 text-yellow-800 text-xs">
                          <Star className="mr-1" size={14} />
                          Featured
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-xl font-semibold text-black">
                        {gallery.title}
                      </h3>
                    </div>

                    {/* Description */}
                    <p className="text-gray-600 mb-3 line-clamp-2">
                      {gallery.description}
                    </p>

                    {/* Metadata */}
                    <div className="space-y-2 mb-3">
                      {gallery.feeling && (
                        <div className="flex items-center text-gray-600">
                          <Smile size={16} className="mr-2 text-red-600" />
                          <span className="capitalize">{gallery.feeling}</span>
                        </div>
                      )}

                      <div className="flex items-center text-gray-600">
                        <Calendar size={16} className="mr-2 text-red-600" />
                        {formatDate(gallery.createdAt)}
                      </div>

                      <div className="flex items-center text-gray-600">
                        <Video size={16} className="mr-2 text-red-600" />
                        {gallery.videos?.length || 0} videos
                      </div>
                    </div>

                    {/* Hashtags */}
                    {gallery.hashtags && gallery.hashtags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {gallery.hashtags.slice(0, 3).map((tag, index) => (
                          <span
                            key={index}
                            className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs"
                          >
                            #{tag}
                          </span>
                        ))}
                        {gallery.hashtags.length > 3 && (
                          <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs">
                            +{gallery.hashtags.length - 3} more
                          </span>
                        )}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-2 mt-3">
                      <button
                        onClick={() => toggleFeatureStatus(gallery)}
                        className="p-2 text-yellow-500 hover:bg-yellow-50 rounded-full transition-colors"
                        title={
                          gallery.featured
                            ? "Remove from featured"
                            : "Add to featured"
                        }
                      >
                        <Star
                          size={20}
                          fill={gallery.featured ? "currentColor" : "none"}
                        />
                      </button>
                      <button
                        onClick={() => openEditModal(gallery)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                        title="Edit gallery"
                      >
                        <Edit2 size={20} />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedGallery(gallery);
                          setShowDeleteModal(true);
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                        title="Delete gallery"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full text-center py-12 bg-white rounded-lg shadow">
                <Video className="mx-auto text-red-300 mb-4" size={64} />
                <p className="text-red-700">
                  No video galleries found. Create one to get started!
                </p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {galleries.length > 0 && totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-red-200 pt-4">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-red-700">
                    Showing{" "}
                    <span className="font-medium">
                      {(currentPage - 1) * itemsPerPage + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-medium">
                      {Math.min(currentPage * itemsPerPage, galleries.length)}
                    </span>{" "}
                    of <span className="font-medium">{galleries.length}</span>{" "}
                    results
                  </p>
                </div>
                <div>
                  <nav
                    className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                    aria-label="Pagination"
                  >
                    <button
                      onClick={() => goToPage(1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-red-300 bg-white text-sm font-medium text-red-500 hover:bg-red-50"
                    >
                      <span className="sr-only">First</span>
                      <ChevronsLeft size={16} />
                    </button>
                    <button
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 border border-red-300 bg-white text-sm font-medium text-red-500 hover:bg-red-50"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeft size={16} />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <button
                          key={page}
                          onClick={() => goToPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === page
                              ? "z-10 bg-red-50 border-red-500 text-red-600"
                              : "bg-white border-red-300 text-red-500 hover:bg-red-50"
                          }`}
                        >
                          {page}
                        </button>
                      )
                    )}
                    <button
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 border border-red-300 bg-white text-sm font-medium text-red-500 hover:bg-red-50"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRight size={16} />
                    </button>
                    <button
                      onClick={() => goToPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-red-300 bg-white text-sm font-medium text-red-500 hover:bg-red-50"
                    >
                      <span className="sr-only">Last</span>
                      <ChevronsRight size={16} />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Delete Video Gallery
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this video gallery? This action
              cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Gallery Form Modal */}
      {showFormModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-black">
                {selectedGallery
                  ? "Edit Video Gallery"
                  : "Add New Video Gallery"}
              </h3>
              <button
                onClick={() => setShowFormModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="title"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Title *
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-red-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="feeling"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Feeling
                  </label>
                  <select
                    id="feeling"
                    name="feeling"
                    value={formData.feeling}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-red-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                  >
                    {feelingOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows={3}
                  value={formData.description}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-red-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                />
              </div>

              {/* Hashtags */}
              <div>
                <label
                  htmlFor="hashtags"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Hashtags
                </label>
                <div className="flex mb-2">
                  <div className="relative flex-grow">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Hash size={16} className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={newHashtag}
                      onChange={(e) => setNewHashtag(e.target.value)}
                      className="flex-1 pl-8 px-3 py-2 border border-red-300 rounded-l-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                      placeholder="Add hashtag"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={addHashtag}
                    className="px-4 py-2 bg-red-600 text-white rounded-r-md hover:bg-red-700"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.hashtags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full bg-red-100 text-red-800 text-sm"
                    >
                      #{tag}
                      <button
                        type="button"
                        onClick={() => removeHashtag(tag)}
                        className="ml-1.5 inline-flex text-red-600 hover:text-black"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Add Video */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Add Video
                </label>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                  <div className="md:col-span-3">
                    <select
                      value={newVideoType}
                      onChange={(e) => setNewVideoType(e.target.value)}
                      className="w-full px-3 py-2 border border-red-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                    >
                      {videoSourceOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-5">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Link size={16} className="text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={newVideoUrl}
                        onChange={(e) => setNewVideoUrl(e.target.value)}
                        placeholder="Video URL"
                        className="w-full pl-8 px-3 py-2 border border-red-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-4">
                    <button
                      type="button"
                      onClick={addVideo}
                      disabled={!newVideoUrl.trim()}
                      className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      Add Video
                    </button>
                  </div>
                </div>

                {/* Video Title and Description */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  <div>
                    <input
                      type="text"
                      value={newVideoTitle}
                      onChange={(e) => setNewVideoTitle(e.target.value)}
                      placeholder="Video title (optional)"
                      className="w-full px-3 py-2 border border-red-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      value={newVideoDescription}
                      onChange={(e) => setNewVideoDescription(e.target.value)}
                      placeholder="Video description (optional)"
                      className="w-full px-3 py-2 border border-red-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                    />
                  </div>
                </div>
              </div>

              {/* Video Preview */}
              {formData.videos.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Gallery Videos ({formData.videos.length})
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {formData.videos.map((video, index) => (
                      <div
                        key={index}
                        className="relative bg-gray-50 border border-red-200 rounded-lg overflow-hidden"
                      >
                        <div className="p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center">
                              {video.videoType === "youtube" && (
                                <Youtube
                                  size={20}
                                  className="text-red-600 mr-2"
                                />
                              )}
                              {video.videoType === "vimeo" && (
                                <Video
                                  size={20}
                                  className="text-blue-600 mr-2"
                                />
                              )}
                              {!["youtube", "vimeo"].includes(
                                video.videoType
                              ) && (
                                <Video
                                  size={20}
                                  className="text-gray-600 mr-2"
                                />
                              )}
                              <h5 className="font-medium text-gray-800 truncate max-w-xs">
                                {video.title || "Untitled Video"}
                              </h5>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeVideo(index)}
                              className="p-1 text-red-500 hover:bg-red-100 rounded-full"
                            >
                              <X size={16} />
                            </button>
                          </div>

                          <p className="text-gray-600 text-sm mb-2 line-clamp-1">
                            {video.description || "No description"}
                          </p>

                          <div className="flex items-center text-xs text-gray-500">
                            <Link size={14} className="mr-1" />
                            <span className="truncate max-w-xs">
                              {video.videoUrl}
                            </span>
                          </div>
                        </div>

                        {/* Video thumbnail if available */}
                        {video.videoType === "youtube" &&
                          getYoutubeVideoId(video.videoUrl) && (
                            <div className="h-40 bg-black">
                              <img
                                src={getYoutubeThumbnail(
                                  getYoutubeVideoId(video.videoUrl)
                                )}
                                alt={video.title || "Video thumbnail"}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Links */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add Links
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Link size={16} className="text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={newLinkUrl}
                        onChange={(e) => setNewLinkUrl(e.target.value)}
                        placeholder="URL"
                        className="w-full pl-8 px-3 py-2 border border-red-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-1">
                    <input
                      type="text"
                      value={newLinkTitle}
                      onChange={(e) => setNewLinkTitle(e.target.value)}
                      placeholder="Link title"
                      className="w-full px-3 py-2 border border-red-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                    />
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={addLink}
                      className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                    >
                      Add Link
                    </button>
                  </div>
                </div>

                {formData.links.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {formData.links.map((link, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-red-50 rounded"
                      >
                        <div className="flex items-center">
                          <Link size={16} className="text-red-600 mr-2" />
                          <span className="text-sm text-black font-medium">
                            {link.title}
                          </span>
                          <span className="ml-2 text-xs text-red-700 truncate max-w-xs">
                            {link.url}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeLink(index)}
                          className="p-1 text-red-500 hover:bg-red-100 rounded-full"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Featured */}
              <div className="flex items-center">
                <input
                  id="featured"
                  name="featured"
                  type="checkbox"
                  checked={formData.featured}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="featured"
                  className="ml-2 block text-sm text-gray-700"
                >
                  Mark as Featured
                </label>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowFormModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formData.videos.length === 0}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {selectedGallery ? "Update Gallery" : "Create Gallery"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Video Preview Modal */}
      {showPreviewModal &&
        selectedGallery &&
        selectedGallery.videos &&
        selectedGallery.videos.length > 0 && (
          <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4 z-50">
            <div className="relative max-w-4xl w-full">
              <button
                onClick={() => setShowPreviewModal(false)}
                className="absolute top-4 right-4 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 z-10"
              >
                <X size={24} />
              </button>

              {/* Video Player */}
              <div className="mb-4">
                <VideoEmbed
                  video={selectedGallery.videos[selectedVideoIndex]}
                />

                {/* Video Title & Description */}
                <div className="bg-black bg-opacity-70 text-white p-4">
                  <h3 className="text-xl font-medium mb-1">
                    {selectedGallery.videos[selectedVideoIndex].title ||
                      selectedGallery.title}
                  </h3>
                  {selectedGallery.videos[selectedVideoIndex].description && (
                    <p className="text-gray-300">
                      {selectedGallery.videos[selectedVideoIndex].description}
                    </p>
                  )}
                </div>
              </div>

              {/* Video Thumbnails */}
              {selectedGallery.videos.length > 1 && (
                <div className="flex overflow-x-auto space-x-2 py-2">
                  {selectedGallery.videos.map((video, index) => (
                    <div
                      key={index}
                      onClick={() => setSelectedVideoIndex(index)}
                      className={`relative flex-shrink-0 w-32 h-20 cursor-pointer ${
                        selectedVideoIndex === index
                          ? "ring-2 ring-red-500"
                          : "opacity-70"
                      }`}
                    >
                      <img
                        src={getVideoThumbnail(video)}
                        alt={video.title || `Video ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Play size={24} className="text-white drop-shadow-lg" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
    </div>
  );
};

export default MarathiVideoGallery;